import classNames from 'classnames';
import React from 'react';
import { Button } from './button';
import { IconChevronLeft, IconClear } from './icons';
import { Inline, Rows } from './layout';
import styles from './panel.css';
import { Scrollable, ScrollableOverflowStyle } from './scrollable';
import { TextRegular } from './typography';

type PanelProps = React.PropsWithChildren;

export function Panel({ children }: PanelProps) {
  return <div className={styles.panel}>{children}</div>;
}

type PanelHeaderProps = {
  title: string;
  navigation: {
    back?: () => void;
    close: () => void;
  };
  extraButton?: React.ReactNode;
};

export function PanelHeader({
  title,
  navigation: { back, close },
  extraButton,
}: PanelHeaderProps) {
  return (
    <div className={classNames(styles.panelHeader, back && styles.hasBack)}>
      {back && (
        <div className={styles.button}>
          <Button
            icon={IconChevronLeft}
            variant="tertiary"
            size="small"
            onClick={back}
          />
        </div>
      )}
      <TextRegular lineClamp={1}>{title}</TextRegular>
      <Inline spacing={8}>
        {extraButton && <div className={styles.button}>{extraButton}</div>}
        <div className={styles.button}>
          <Button
            icon={IconClear}
            variant="subtle"
            size="small"
            onClick={close}
          />
        </div>
      </Inline>
    </div>
  );
}

export function PanelScrollable({
  children,
  hide,
  hideTopPadding = false,
  overflowStyle = 'none',
  hideScrollBar = true,
}: {
  children: React.ReactNode;
  hide?: boolean;
  hideTopPadding?: boolean;
  overflowStyle?: ScrollableOverflowStyle;
  hideScrollBar?: boolean;
}) {
  return (
    <div
      className={classNames(styles.panelScrollable, hide && styles.isHidden)}
    >
      <Scrollable overflowStyle={overflowStyle} hideScrollBar={hideScrollBar}>
        <div style={{ paddingBottom: 16, paddingTop: hideTopPadding ? 0 : 16 }}>
          {children}
        </div>
      </Scrollable>
    </div>
  );
}

export function PanelLayout({
  header,
  footer,
  children,
  disableWrapChildren,
  hideHeaderDivider = false,
  hideFooterDivider = false,
  ...headerProps
}: {
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  disableWrapChildren?: boolean;
  hideHeaderDivider?: boolean;
  hideFooterDivider?: boolean;
} & PanelHeaderProps) {
  return (
    <Rows spacing={0} fixedHeight>
      <Rows spacing={12}>
        <PanelHeader {...headerProps} />
        {header}
        {!hideHeaderDivider && <DividerLine expandToEdges={true} />}
      </Rows>
      {disableWrapChildren ? (
        children
      ) : (
        <PanelScrollable>{children}</PanelScrollable>
      )}

      {footer && (
        <Rows spacing={12}>
          {!hideFooterDivider && <DividerLine expandToEdges={true} />}
          <div className={styles.panelFooter}>{footer}</div>
        </Rows>
      )}
    </Rows>
  );
}

export function DividerLine({ expandToEdges }: { expandToEdges?: boolean }) {
  return (
    <div
      className={classNames(
        styles.dividerLine,
        expandToEdges && styles.expandToEdges,
      )}
    />
  );
}

type ExpandToEdgesProps = React.PropsWithChildren<{}>;

export function ExpandToEdges({ children }: ExpandToEdgesProps) {
  return <div className={styles.expandToEdges}>{children}</div>;
}

export function DividerLineWithText({
  text,
  direction = 'horizontal',
  TextComponent = TextRegular,
}: {
  text: React.ReactNode;
  direction?: 'horizontal' | 'vertical';
  TextComponent?: React.ComponentType<any>;
}) {
  return (
    <div className={classNames(styles.dividerLineWithText, styles[direction])}>
      <span className={styles.line} />
      <TextComponent>{text}</TextComponent>
      <span className={styles.line} />
    </div>
  );
}
